import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { cn } from "@fieldwork/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import z from "zod";

import { NotConnected, useServer } from "@/components/server-hooks";
import { ServerPage } from "@/components/server-shell";
import { Chip, ago } from "@/components/ui-bits";
import { act, useAgentAction } from "@/lib/act";

const STATUSES = ["suggested", "spam", "blocked", "unsubscribed", "useful"] as const;
type Status = (typeof STATUSES)[number];

export const Route = createFileRoute("/_auth/servers/$id/inbox")({
  validateSearch: z.object({ tab: z.enum(STATUSES).optional(), account: z.string().optional(), q: z.string().optional() }),
  component: Inbox,
});

type Sender = {
  account: string;
  address: string;
  display: string | null;
  n: number;
  unread: number;
  last_seen: string | null;
  subjects: string[];
  categories: string | null;
  action_hint: string | null;
  reason: string | null;
  status: Status;
  status_at: string | null;
  busy: number;
  last_result: string | null;
  unsub_https: string | null;
  unsub_mailto: boolean;
  one_click: number | null;
  also_in: string[];
  gmail_url: string;
};
type List = {
  tab: Status;
  counts: Partial<Record<Status, number>>;
  rows: Sender[];
  scan_errors: { account: string; last_error: string; error_at: string }[];
  last_scan: string | null;
  scanning: boolean;
  working: boolean;
  accounts: { account: string; address: string; missing: string[] }[];
  ready: boolean;
};

const TABS: [Status, string][] = [
  ["suggested", "Suggested"],
  ["spam", "Spam"],
  ["blocked", "Blocked"],
  ["unsubscribed", "Unsubscribed"],
  ["useful", "Useful"],
];
const HINT: Record<string, Status> = { spam: "spam", block: "blocked", unsubscribe: "unsubscribed" };
const MOVE: [Status, string][] = [
  ["spam", "Spam"],
  ["blocked", "Block"],
  ["unsubscribed", "Unsubscribe"],
  ["useful", "Useful"],
  ["suggested", "Back to suggestions"],
];
const key = (r: Pick<Sender, "account" | "address">) => `${r.account}|${r.address}`;
const day = (ts: string | null) => (ts ? new Date(ts).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "?");

function useInboxList(id: string, connected: boolean) {
  const search = Route.useSearch();
  const params = { tab: search.tab ?? "suggested", account: search.account ?? "", q: search.q ?? "" };
  return useQuery({
    queryKey: ["act", id, "inbox_list", params],
    queryFn: () => act<List>(id, "inbox_list", params),
    enabled: connected,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && (d.working || d.scanning || d.rows.some((r) => r.busy)) ? 4000 : false;
    },
  });
}

function InboxBody({ id, connected, q }: { id: string; connected: boolean; q: ReturnType<typeof useInboxList> }) {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab = search.tab ?? "suggested";
  const [text, setText] = useState(search.q ?? "");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const move = useAgentAction<{ status: Status; jobs: { account: string; address: string }[] }, { started: number }>(
    id,
    "inbox_act",
    (v) => v,
    { refresh: ["inbox_list"], success: (r) => `Working on ${r.started} sender${r.started > 1 ? "s" : ""}…` },
  );

  if (!connected) return <NotConnected />;
  if (q.error) return <p className="text-sm text-[#F48A80]">{q.error.message}</p>;
  if (!q.data) return <p className="text-sm text-[#A8A59D]">Loading…</p>;
  const d = q.data;
  const need = d.accounts.filter((a) => a.missing.length);
  const go = (status: Status, jobs: { account: string; address: string }[]) =>
    move.mutate({ status, jobs }, { onSuccess: () => setSel(new Set()) });
  const rowsByKey = new Map(d.rows.map((r) => [key(r), r]));
  const selected = [...sel].filter((k) => rowsByKey.has(k));
  const allOn = d.rows.length > 0 && selected.length === d.rows.length;

  return (
    <>
      {need.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-md bg-[rgba(232,163,61,0.08)] px-[18px] py-3.5 shadow-[inset_0_0_0_1px_rgba(232,163,61,0.35)]">
          <span className="text-[#CFCBC1]">
            <span className="font-semibold text-[#F0B65A]">{need.map((a) => a.address).join(", ")}</span> can be scanned but not cleaned
            up yet: it was connected before cleanup access existed. Useful works without it.
          </span>
          <Link to="/servers/$id/sources" params={{ id }} className="font-mono text-[13px] no-underline">
            Reconnect with cleanup access →
          </Link>
        </div>
      )}
      {d.scan_errors.map((e) => (
        <p key={e.account} className="m-0 rounded-md bg-[rgba(240,100,90,0.08)] px-[18px] py-3.5 text-[#F48A80] shadow-[inset_0_0_0_1px_rgba(240,100,90,0.35)]">
          Last scan of {e.account} failed {ago(e.error_at)}: {e.last_error.slice(0, 200)}
        </p>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Sender lists" className="flex flex-wrap gap-1.5">
          {TABS.map(([t, label]) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={t === tab}
              onClick={() => {
                setSel(new Set());
                navigate({ search: { ...search, tab: t === "suggested" ? undefined : t } });
              }}
              className={cn("min-h-9 rounded-[4px] px-3 font-mono text-xs", t === tab ? "bg-[#ECEAE4] text-[#111111]" : "bg-[#262523] text-[#CFCBC1]")}
            >
              {label} {d.counts[t] ?? 0}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {d.accounts.length > 1 && (
            <label className="flex">
              <span className="sr-only">Account</span>
              <select
                className="h-10 rounded-[4px] border border-[#3A3935] bg-[#1A1A19] px-3 text-sm text-[#ECEAE4]"
                value={search.account ?? ""}
                onChange={(e) => navigate({ search: { ...search, account: e.target.value || undefined } })}
              >
                <option value="">All accounts</option>
                {d.accounts.map((a) => (
                  <option key={a.account} value={a.account}>
                    {a.address}
                  </option>
                ))}
              </select>
            </label>
          )}
          <form
            className="flex"
            onSubmit={(e) => {
              e.preventDefault();
              navigate({ search: { ...search, q: text.trim() || undefined } });
            }}
          >
            <label className="flex">
              <span className="sr-only">Search senders</span>
              <Input type="search" className="h-10 w-60 max-w-full bg-[#1A1A19]" placeholder="Search sender, subject…" value={text} onChange={(e) => setText(e.target.value)} />
            </label>
          </form>
        </div>
      </div>

      {d.rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-md bg-[#1A1A19] px-4 py-2.5 shadow-[inset_0_0_0_1px_#2A2927]">
          <label className="flex min-h-10 items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              className="size-4 accent-[#FF4F1A]"
              checked={allOn}
              onChange={() => setSel(allOn ? new Set() : new Set(d.rows.map(key)))}
            />
            {selected.length ? `${selected.length} selected` : "Select all"}
          </label>
          {MOVE.filter(([st]) => st !== tab && st !== "unsubscribed").map(([st, label]) => (
            <Button
              key={st}
              variant="outline"
              size="sm"
              disabled={!selected.length || move.isPending}
              onClick={() => go(st, selected.map((k) => rowsByKey.get(k)!))}
            >
              {st === "useful" ? "Mark useful" : st === "suggested" ? "Back to suggestions" : `${label} selected`}
            </Button>
          ))}
        </div>
      )}

      {!d.ready && <p className="text-sm text-[#A8A59D]">The first inbox scan hasn't run yet. Click Rescan now, or wait for tonight's scan.</p>}
      {d.ready && d.rows.length === 0 && <p className="text-sm text-[#A8A59D]">Nothing here.</p>}
      {d.rows.map((r) => (
        <SenderCard
          key={key(r)}
          r={r}
          checked={sel.has(key(r))}
          toggle={() => {
            const s = new Set(sel);
            if (s.has(key(r))) s.delete(key(r));
            else s.add(key(r));
            setSel(s);
          }}
          go={go}
          pending={move.isPending}
        />
      ))}

      <p className="m-0 text-[13px] text-[#A8A59D]">
        Spam reports a sender's mail from the last year; Block adds a Gmail filter and archives; Unsubscribe uses the sender's
        one-click link and archives; Useful keeps them off this list. Moving a sender again undoes the previous move, except
        Unsubscribe. Nothing is sent or deleted.
      </p>
    </>
  );
}

function SenderCard({
  r,
  checked,
  toggle,
  go,
  pending,
}: {
  r: Sender;
  checked: boolean;
  toggle: () => void;
  go: (status: Status, jobs: { account: string; address: string }[]) => void;
  pending: boolean;
}) {
  const hint = HINT[r.action_hint ?? ""];
  const moves = MOVE.filter(([st]) => st !== r.status && !(st === "unsubscribed" && !r.unsub_https && !r.unsub_mailto)).sort(
    ([a], [b]) => Number(b === hint) - Number(a === hint),
  );
  const busy = !!r.busy;
  return (
    <article
      className={cn(
        "flex gap-4 rounded-md bg-[#1A1A19] px-[22px] py-[18px]",
        checked ? "shadow-[inset_0_0_0_1px_#3A3935]" : "shadow-[inset_0_0_0_1px_#2A2927]",
      )}
    >
      <input
        type="checkbox"
        aria-label={`Select ${r.display || r.address}`}
        className="mt-[3px] size-4 shrink-0 accent-[#FF4F1A]"
        checked={checked}
        onChange={toggle}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="m-0 text-[15px] font-semibold">{r.display || r.address}</h3>
          <span className="font-mono text-xs text-[#A8A59D]">{r.address}</span>
          <Chip value="neutral" label={r.account} />
          {(r.categories ?? "")
            .split(",")
            .filter(Boolean)
            .map((c) => (
              <Chip key={c} value="neutral" label={c} />
            ))}
          {r.status === "suggested" && hint && <Chip value="warn" label={`suggest: ${r.action_hint}`} />}
          {busy && <Chip value="info" label="working…" />}
        </div>
        <span className="text-[13px] text-[#A8A59D]">
          {r.n} mails in 90 days{r.n ? ` · ${Math.round((100 * r.unread) / r.n)}% unread` : ""} · last {day(r.last_seen)}
          {r.also_in.length > 0 && ` · also in ${r.also_in.join(", ")}`} ·{" "}
          <a href={r.gmail_url} target="_blank" rel="noopener noreferrer">
            open in Gmail ↗
          </a>
          {r.unsub_https && !r.one_click && (
            <>
              {" · "}
              <a href={r.unsub_https} target="_blank" rel="noopener noreferrer">
                their unsubscribe page ↗
              </a>{" "}
              (open it, then click Unsubscribe here to archive)
            </>
          )}
          {r.unsub_mailto && !r.unsub_https && " · unsubscribe is by email only: use Gmail's Unsubscribe link"}
        </span>
        {r.reason && <span className="text-[#CFCBC1]">{r.reason}</span>}
        {r.subjects.length > 0 && (
          <ul className="m-0 pl-[18px] text-[13px] text-[#A8A59D]">
            {r.subjects.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        )}
        {!busy && (
          <div className="flex flex-wrap gap-2 pt-1">
            {moves.map(([st, label]) => (
              <Button
                key={st}
                size="sm"
                variant={st === hint && r.status === "suggested" ? "default" : "outline"}
                disabled={pending}
                onClick={() => go(st, [r])}
              >
                {label}
              </Button>
            ))}
            {r.status === "suggested" && r.also_in.length > 0 && (hint === "spam" || hint === "blocked") && (
              <Button
                size="sm"
                variant="outline"
                title={`Same action in ${r.also_in.join(", ")}`}
                disabled={pending}
                onClick={() => go(hint, [{ account: "*", address: r.address }])}
              >
                {hint === "spam" ? "Spam" : "Block"} in all accounts
              </Button>
            )}
          </div>
        )}
        {r.last_result && (
          <span className={cn("text-xs", r.last_result.startsWith("failed") ? "text-[#F48A80]" : "text-[#A8A59D]")}>
            {r.last_result}
            {r.status_at ? ` · ${ago(r.status_at)}` : ""}
          </span>
        )}
      </div>
    </article>
  );
}

function Inbox() {
  const { id } = Route.useParams();
  const connected = !!useServer(id).data?.hub.connected;
  const q = useInboxList(id, connected);
  const d = q.data;
  const scan = useAgentAction(id, "inbox_scan", () => ({}), {
    refresh: ["inbox_list"],
    success: "Scan started; new suggestions appear here when it finishes",
  });
  return (
    <ServerPage
      title="Inbox cleanup"
      subtitle={
        <span className="block max-w-[72ch]">
          Senders from the last 90 days that look like spam or distractions. People you've written to, people in People and
          senders behind a task are never suggested.
          {d && (d.scanning ? " Scanning now…" : ` Last scan ${ago(d.last_scan)}.`)}
        </span>
      }
      actions={
        connected &&
        d && (
          <Button variant="outline" disabled={scan.isPending || d.scanning} onClick={() => scan.mutate({})}>
            {d.scanning ? "Scanning…" : "Rescan now"}
          </Button>
        )
      }
    >
      <InboxBody id={id} connected={connected} q={q} />
    </ServerPage>
  );
}
